import { Accessor, createEffect, on } from 'solid-js';
import {
  MarkupsCapabilityProvider,
  PointCloudCapabilityProvider,
  ViewsCapabilityProvider,
  TrimBimCapabilityProvider,
  getTrbModelName,
} from '@trimble/web3d-providers';
import { ClippingCapability } from '@trimble/web3d-capabilities';
import {
  CapabilityCoordinatorBase,
  CapabilityRegistry,
  FitToViewOnloadCapabilityCoordinator,
} from '@trimble/web3d';
import { TrimbimModel, IMetaMaterial } from '@technology/web3d-plugin-trimbim';
import type { ConnectIdentifierResolver } from '@technology/web3d-plugin-trimbim/dist/esnext/Extras/ConnectIdentifier/ConnectIdentifierResolver';
import { Color } from 'three';
import { Element as PresentationElement } from '@data-access/connect/tc-tcps';
import { getLogger } from '@trimble/logging';
import { ViewModeCapabilityCoordinator } from './view-mode-capability-coordinator';
import { PaneVisibilityCoordinator } from './pane-visibility-coordinator';
import { applyPositioningData } from './views-loading-coordinator.helpers';
import {
  buildResolver,
  resolveSourceIdToEntityIds,
} from '@trimble/tc-issue-management-web3d-utilities';

const MAX_UINT8_VALUE = 255;
const PERCENTAGE_SCALE = 100;

const levelToOpacityFraction = (level: number | null): number =>
  level != null ? level / PERCENTAGE_SCALE : 1;

/**
 * Coordinator that applies view state
 */
export class ViewsLoadingCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private readonly affectedModelIds = new Map<
    TrimBimCapabilityProvider,
    Set<string>
  >();
  private readonly pendingOperations = new Map<
    TrimBimCapabilityProvider,
    Promise<void>
  >();

  constructor(
    registry: CapabilityRegistry,
    viewsProvider: ViewsCapabilityProvider,
    visiblePointCloudIds: Accessor<string[] | undefined>
  ) {
    super(registry);

    createEffect(
      on(
        visiblePointCloudIds,
        (ids) => {
          if (ids !== undefined && ids.length === 0) {
            registry.get(MarkupsCapabilityProvider)()?.removeAllMarkups();
          }
        },
        { defer: true }
      )
    );

    createEffect(() => {
      const elements = viewsProvider.getPresentationElements();

      for (const container of registry.paneContainers()) {
        const viewModeCoord = container.get(ViewModeCapabilityCoordinator)();
        if (viewModeCoord && viewModeCoord.viewMode().type !== '3D') {
          continue;
        }

        const trbProvider = container.get(TrimBimCapabilityProvider)();
        if (!trbProvider) continue;

        // re-runs when models finish loading.
        const loadedModels = trbProvider.items();
        if (loadedModels.length === 0) continue;

        if (elements && elements.length > 0) {
          this.chainOperation(trbProvider, async () => {
            await this.resetAffectedMaterials(trbProvider);
            await this.applyPresentationElements(elements, trbProvider);
          });
        } else {
          this.chainOperation(trbProvider, () =>
            this.resetAffectedMaterials(trbProvider)
          );
        }
      }
    });

    // Intentional bare `createEffect` (NOT wrapped in `on(() => getPositionData(), …)`).
    //
    // The bare effect tracks BOTH `viewsProvider.getPositionData()` and the
    // implicit `registry.paneContainers()` read in the loop below. That second
    // dependency is load-bearing: when a pane's container changes (e.g. the
    // user switches a pane to Panorama mode and a new container is registered),
    // the effect re-runs and re-applies `applyPositioningData` to all panes —
    // which nudges other panes' alignment/station capabilities back into the
    // state implied by the loaded view.
    //
    // Without this re-fire, e2e combo #16 (`profileView × Panorama`,
    // `panorama_to_profileView__pane0__afterRotate`) goes from Playwright-flaky
    // to deterministic 3-retry failure with a consistent ~3 unit position drift
    // on pane 0 (see commit b407935e8 / 00f60b74f for the revert).
    //
    // Static analysis tools may flag the implicit `paneContainers()` tracking
    // as over-tracking — it isn't; it's the recovery mechanism the saved-view
    // application relies on. If you want to remove it, first fix the underlying
    // alignment-state issue that makes the recovery necessary, or scope the
    // re-fire to container *additions* only (not user navigation changes).
    createEffect(() => {
      const positionData = viewsProvider.getPositionData();
      if (!positionData) return;

      for (const container of registry.paneContainers()) {
        void applyPositioningData(container, positionData);
        container
          .get(FitToViewOnloadCapabilityCoordinator)()
          ?.suppressFitToView(container);
      }
    });

    // Models: 3D and image panes display them; the per-pane effective level
    // resolves to either upstream (global slider) when linked, or the
    // pane's local override after it unlinks via slider movement. We
    // gate on PaneVisibilityCoordinator up front so panes whose
    // registration hasn't completed yet (or test fixtures without a
    // pane cap) are silently skipped.
    //
    // While a pane is live this is the sole writer of model visibility and
    // model opacity (teardown resets both in `cleanup`): an image pane hides
    // its models both at clip distance 0 and at transparency level 0, and a
    // split owner would let a slider move un-hide a pane whose clip box is off.
    createEffect(() => {
      for (const container of registry.paneContainers()) {
        const paneCap = container.get(PaneVisibilityCoordinator)();
        if (!paneCap) continue;

        const viewModeCoord = container.get(ViewModeCapabilityCoordinator)();
        // A pane without a view mode coordinator is a 3D pane in test fixtures.
        const type = viewModeCoord?.viewMode().type ?? '3D';
        // Map panes render no models; leave their viewer settings untouched.
        if (type === '2D') continue;

        const trbProvider = container.get(TrimBimCapabilityProvider)();
        if (!trbProvider) continue;

        const isImageMode = type === 'Panorama' || type === 'Image';
        const transparencyLevel = paneCap.models.effective();
        // Debounced like the clip box it gates, so raising the distance from
        // 0 cannot show the models before the box is rebuilt.
        const clipDistance = paneCap.clipDistance.effectiveDebounced();

        trbProvider.setIsModelsVisible(
          !(isImageMode && (clipDistance === 0 || transparencyLevel === 0))
        );
        trbProvider.viewer.settings.globalOpacity =
          transparencyLevel === 0
            ? 1
            : levelToOpacityFraction(transparencyLevel);
      }
    });

    // Point clouds: applied to every pane regardless of view mode (3D /
    // Panorama / Planar). Each pane reads its own per-pane effective
    // value through PaneVisibilityCoordinator. The provider skips drawing
    // a fully transparent cloud (opacity 0) but keeps it pickable.
    createEffect(() => {
      for (const container of registry.paneContainers()) {
        const paneCap = container.get(PaneVisibilityCoordinator)();
        if (!paneCap) continue;

        const opacity = levelToOpacityFraction(paneCap.pointClouds.effective());
        const pcProvider = container.get(PointCloudCapabilityProvider)();
        if (pcProvider) {
          pcProvider.setOpacity(opacity);
          continue;
        }

        const wrapper = container.viewerWrapper();
        if (!wrapper) continue;
        wrapper.potreeManager.opacity = opacity;
      }
    });

    // Station opacity is owned by `ImageCapabilityBase` (preload visibility).
    // The opacityOptions bag reads from the pane's
    // PaneVisibilityCoordinator.stations.effective() — see
    // station-transparency-helpers.ts.
  }

  private chainOperation(
    trbProvider: TrimBimCapabilityProvider,
    operation: () => Promise<void>
  ): void {
    const previous =
      this.pendingOperations.get(trbProvider) ?? Promise.resolve();
    const next = previous.then(operation, operation);
    this.pendingOperations.set(trbProvider, next);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 21 > 15 allowed; flagged for later refactor, not fixed here
  private async applyPresentationElements(
    elements: PresentationElement[],
    trbProvider: TrimBimCapabilityProvider
  ): Promise<void> {
    // Resolver build is expensive; cache per model for this call.
    const resolverCache = new Map<string, ConnectIdentifierResolver>();

    for (const element of elements) {
      if (!element.versionId) continue;

      const hasColor =
        element.color?.r !== undefined &&
        element.color?.g !== undefined &&
        element.color?.b !== undefined;
      const hasOpacity = element.color?.a !== undefined;

      if (!hasColor && !hasOpacity) continue;

      const trbModelName = getTrbModelName(element.versionId);
      const model = trbProvider.viewer.getModel(trbModelName) as
        | TrimbimModel
        | undefined;
      if (!model) continue;

      try {
        let resolver = resolverCache.get(trbModelName);
        if (!resolver) {
          resolver = await buildResolver(model);
          resolverCache.set(trbModelName, resolver);
        }

        const validEntityIds = resolveSourceIdToEntityIds(
          resolver,
          element.sourceId
        );
        if (validEntityIds.length === 0) continue;

        const material: IMetaMaterial = {};
        if (hasOpacity) {
          material.opacity = element.color.a / MAX_UINT8_VALUE;
        }
        if (hasColor) {
          material.color = new Color(
            element.color.r / MAX_UINT8_VALUE,
            element.color.g / MAX_UINT8_VALUE,
            element.color.b / MAX_UINT8_VALUE
          );
        }
        await model.setCustomMaterial(validEntityIds, material);
        let modelIds = this.affectedModelIds.get(trbProvider);
        if (!modelIds) {
          modelIds = new Set();
          this.affectedModelIds.set(trbProvider, modelIds);
        }
        modelIds.add(trbModelName);
      } catch (error) {
        getLogger().error(
          `Failed to apply presentation for element ${element.sourceId}:`,
          error
        );
      }
    }
  }

  private async resetAffectedMaterials(
    trbProvider: TrimBimCapabilityProvider
  ): Promise<void> {
    const modelIds = this.affectedModelIds.get(trbProvider);
    if (!modelIds) return;

    for (const modelId of modelIds) {
      try {
        const model = trbProvider.viewer.getModel(modelId) as
          | TrimbimModel
          | undefined;
        if (model) {
          await model.resetCustomMaterials();
        }
      } catch (error) {
        getLogger().error(
          `Failed to reset materials for model ${modelId}:`,
          error
        );
      }
    }
    modelIds.clear();
  }

  public async cleanup(): Promise<void> {
    await Promise.all(this.pendingOperations.values());
    const resets = [...this.affectedModelIds.entries()].map(([trbProvider]) =>
      this.resetAffectedMaterials(trbProvider)
    );
    await Promise.all(resets);

    for (const container of this.registry.paneContainers()) {
      const clippingCapability = container.get(ClippingCapability)();
      if (clippingCapability) {
        clippingCapability.clearAll();
      }

      const trbProvider = container.get(TrimBimCapabilityProvider)();
      if (trbProvider) {
        trbProvider.viewer.settings.globalOpacity = 1;
        trbProvider.setIsModelsVisible(true);
      }

      // Reset through the provider so a cloud not drawn at opacity 0 is
      // drawn again; the potree manager is the fallback without a provider.
      const pcProvider = container.get(PointCloudCapabilityProvider)();
      if (pcProvider) {
        pcProvider.setOpacity(1);
      } else {
        const wrapper = container.viewerWrapper();
        if (wrapper) {
          wrapper.potreeManager.opacity = 1;
        }
      }
      // Station image opacity is owned by `ImageCapabilityBase`; its own
      // cleanup unloads the loaded stations, so a reset here would race
      // with — or be shadowed by — that unload.
    }
  }
}
